import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  IconButton,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { Campaign, Department } from '../types';
import { useAuth } from '../contexts/AuthContext';
import moment from 'moment-timezone';
import { exportDailySummaryToCSV, exportDailySummaryToExcel, exportDailySummaryToPDF } from '../utils/exportUtils';
import { CampaignFilterService } from '../services/campaignFilterService';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface ReportFilters {
  startDate: string;
  endDate: string;
  departmentId: string;
  campaignId: string;
  page: number;
  limit: number;
}

interface ReportResponse {
  success: boolean;
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const DailySummaryReportPage: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    departmentId: '',
    campaignId: '',
    page: 1,
    limit: 25,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee'|'date'|'campaign'|'clockIn'|'clockOut'|'work'|'break'|'lunch'>('date');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch departments for filter dropdown (not available for agents)
  const { data: departmentsData } = useQuery<{ data: { success: boolean; data: Department[] } }, Error>({
    queryKey: ['departments'],
    queryFn: apiClient.departments.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch campaigns for filter dropdown (not available for agents)
  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Check if user is an agent (hide filters for agents)
  const isAgent = user?.role === 'agent';

  // Filter departments and campaigns based on user role and assigned campaigns
  const availableDepartments = departmentsData?.data.data ? 
    (user && ['team_lead', 'supervisor'].includes(user.role) ? 
      departmentsData.data.data.filter(dept => dept.id === user.departmentId) :
      departmentsData.data.data) : [];

  const availableCampaigns = campaignsData?.data.data ? 
    (user ? CampaignFilterService.getUserAccessibleCampaigns(user, campaignsData.data.data) : []) : [];

  // Get report data
  const { data: reportResponse, isLoading, error, refetch } = useQuery<ReportResponse>({
    queryKey: ['dailySummaryReport', filters],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getDailySummary(filters);
      return response.data;
    },
    enabled: true,
  });

  const handleFilterChange = (field: keyof ReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : prev.page),
    }));
  };

  const handleSearch = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      departmentId: '',
      campaignId: '',
      page: 1,
      limit: 25,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handleFilterChange('page', value);
  };

  const getWorkTimeColor = (hours: number) => {
    if (hours >= 8) return 'success';
    if (hours >= 6) return 'warning';
    return 'error';
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllDailySummariesForExport = async () => {
    const baseParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      departmentId: filters.departmentId || undefined,
      campaignId: filters.campaignId || undefined,
      page: 1,
      limit: 1000,
    };

    let allRecords: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getDailySummary({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllDailySummariesForExport();
      exportDailySummaryToCSV(data, filters);
    } catch (error) {
      console.error('Error exporting daily summary CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllDailySummariesForExport();
      exportDailySummaryToExcel(data, filters);
    } catch (error) {
      console.error('Error exporting daily summary Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllDailySummariesForExport();
      exportDailySummaryToPDF(data, filters);
    } catch (error) {
      console.error('Error exporting daily summary PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const toggleSort = (col:'employee'|'date'|'campaign'|'clockIn'|'clockOut'|'work'|'break'|'lunch') => {
    if (sortBy===col) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortBy(col); setSortDir(col==='date'?'desc':'asc'); }
  };

  const formatSummaryDate = (dateString?: string, timezone?: string) => {
    if (!dateString) {
      return 'N/A';
    }
    const tz = timezone || user?.timezone || 'UTC';
    return moment.tz(dateString, 'YYYY-MM-DD', tz).format('MMM DD, YYYY');
  };

  const getSummaryDateValue = (dateString?: string, timezone?: string) => {
    if (!dateString) {
      return 0;
    }
    const tz = timezone || user?.timezone || 'UTC';
    return moment.tz(dateString, 'YYYY-MM-DD', tz).valueOf();
  };

  const sortedSummaries = React.useMemo(()=>{
    const list = reportResponse?.data ? [...reportResponse.data] : [];
    list.sort((a:any,b:any)=>{
      let av:any=''; let bv:any='';
      switch (sortBy){
        case 'employee':
          // Sort by actual worker (for payroll), not covered employee
          const aUser = a.user;
          const bUser = b.user;
          av = `${aUser?.lastName||''}${aUser?.firstName||''}`.toLowerCase();
          bv = `${bUser?.lastName||''}${bUser?.firstName||''}`.toLowerCase();
          break;
        case 'campaign':
          av = (a.campaign?.name||'').toLowerCase();
          bv = (b.campaign?.name||'').toLowerCase();
          break;
        case 'clockIn':
          av = a.clockIn ? new Date(a.clockIn).getTime() : 0;
          bv = b.clockIn ? new Date(b.clockIn).getTime() : 0;
          break;
        case 'clockOut':
          av = a.clockOut ? new Date(a.clockOut).getTime() : 0;
          bv = b.clockOut ? new Date(b.clockOut).getTime() : 0;
          break;
        case 'work':
          av = a.totalWorkTime||0; bv = b.totalWorkTime||0; break;
        case 'break':
          av = a.totalBreakTime||0; bv = b.totalBreakTime||0; break;
        case 'lunch':
          av = a.totalLunchTime||0; bv = b.totalLunchTime||0; break;
        case 'date':
        default:
          av = getSummaryDateValue(a.date, a.user?.timezone);
          bv = getSummaryDateValue(b.date, b.user?.timezone);
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir==='asc' ? cmp : -cmp;
    });
    return list;
  },[reportResponse?.data, sortBy, sortDir]);


  const activeFilters = Object.entries(filters).filter(([key, value]) => 
    value !== '' && value !== 0 && key !== 'page' && key !== 'limit'
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Daily Summary Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Department"
                    value={filters.departmentId}
                    onChange={(value) => handleFilterChange('departmentId', String(value))}
                    options={availableDepartments.map((d: Department) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                </Grid>
              )}

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Campaign"
                    value={filters.campaignId}
                    onChange={(value) => handleFilterChange('campaignId', String(value))}
                    options={availableCampaigns.map((c: Campaign) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', value as number)}
                  options={[10, 25, 50, 100].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={isLoading}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!reportResponse?.data || reportResponse.data.length === 0}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => setShareDialogOpen(true)}
                      disabled={!reportResponse?.data || reportResponse.data.length === 0}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Share
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {activeFilters.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeFilters.map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    onDelete={() => handleFilterChange(key as keyof ReportFilters, '')}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Daily Summary
              {reportResponse?.pagination && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({reportResponse.pagination.total} total records)
                </Typography>
              )}
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load daily summary report
            </Alert>
          )}

          {reportResponse && !isLoading && (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('employee')}>Employee {sortBy==='employee'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('date')}>Date {sortBy==='date'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('campaign')}>Campaign {sortBy==='campaign'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('clockIn')}>Clock In {sortBy==='clockIn'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('clockOut')}>Clock Out {sortBy==='clockOut'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <Tooltip 
                        title="Work Time: Total hours worked calculated from clock-in to clock-out, with all break periods (break_start to break_end) and lunch periods (lunch_start to lunch_end) subtracted from the total. This represents the actual productive work time."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('work')}>
                          Work Time {sortBy==='work'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                      <Tooltip 
                        title="Break Time: Sum of all break periods (break_start to break_end) throughout the day. Each break period is calculated from when the employee starts their break until they end it."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('break')}>
                          Break Time {sortBy==='break'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                      <Tooltip 
                        title="Lunch Time: Sum of all lunch periods (lunch_start to lunch_end) throughout the day. Each lunch period is calculated from when the employee starts their lunch until they end it."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('lunch')}>
                          Lunch Time {sortBy==='lunch'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSummaries.map((summary: any, index: number) => {
                      // Show the actual person who worked (for payroll)
                      const displayUser = summary.user;
                      const isCoverage = summary.isCoverage || false;
                      const coveredEmployee = summary.coveredEmployee; // The person being covered
                      
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {displayUser?.displayName || `${displayUser?.firstName || ''} ${displayUser?.lastName || ''}`.trim()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {displayUser?.employeeId} | {displayUser?.role}
                                  {isCoverage && coveredEmployee && (
                                    <span> • Covering for: {coveredEmployee.displayName || `${coveredEmployee.firstName} ${coveredEmployee.lastName}`}</span>
                                  )}
                                </Typography>
                              </Box>
                              {isCoverage && (
                                <Tooltip title={`This employee is covering for ${coveredEmployee?.displayName || 'another employee'}. This time entry is for payroll purposes and shows the actual worker.`}>
                                  <PersonAddIcon 
                                    color="primary" 
                                    fontSize="small" 
                                    sx={{ 
                                      cursor: 'help',
                                      flexShrink: 0
                                    }} 
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        <TableCell>
                          {formatSummaryDate(summary.date, summary.user?.timezone)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {summary.campaign?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {summary.clockInFormatted || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {summary.clockOutFormatted || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Work Time: ${summary.totalWorkTime} hours. Calculated from clock-in to clock-out, with ${summary.totalBreakTime} minutes of breaks and ${summary.totalLunchTime} minutes of lunch subtracted.`}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={`${summary.totalWorkTime}h`}
                              color={getWorkTimeColor(summary.totalWorkTime)}
                              size="small"
                              icon={<AccessTimeIcon />}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Break Time: ${summary.totalBreakTime} minutes total. This is the sum of all break periods (break_start to break_end) throughout the day.`}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={`${summary.totalBreakTime}m`}
                              color="info"
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Lunch Time: ${summary.totalLunchTime} minutes total. This is the sum of all lunch periods (lunch_start to lunch_end) throughout the day.`}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={`${summary.totalLunchTime}m`}
                              color="info"
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {reportResponse.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportResponse.pagination.totalPages}
                    page={reportResponse.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {reportResponse && reportResponse.data.length === 0 && !isLoading && (
            <Alert severity="info">
              No daily summary data found matching the selected criteria.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="daily-summary"
          reportFilters={filters}
          reportTitle="Daily Summary Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default DailySummaryReportPage;
